import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { firstValueFrom, Observable, Subject, timer } from 'rxjs';
import { map, repeat, switchMap, takeUntil, tap     } from 'rxjs/operators';

import { QueryCollectorService, SessionStorageService } from '@shared/services';
import { getStatusText, HiringStateEnum, ReferralProgramRefereesResponse } from '@shared/models';
import { Referee } from '@shared/factories';

import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ReferralsService {
          start$ = new Subject<void>();
  private stop$  = new Subject<void>();

  constructor(
    private http:                  HttpClient,
    private sessionStorageService: SessionStorageService,
    private queryCollectorService: QueryCollectorService
  ) { }

  static ACTIVE_API       = ()               => `${environment.apiUrl}api/portal/v3/referral_program_referees/dashboard`;
  static ARCHIVE_API      = ()               => `${environment.apiUrl}api/portal/v3/referral_program_referees/archive`;
  static ARCHIVE_ITEM_API = (itemId: number) => `${environment.apiUrl}api/portal/v3/referral_program_referees/${itemId}/review`;

  getActives(): Observable<Referee[]> {
    return timer(0).pipe(
      switchMap(() => this.request('dashboard')),
      takeUntil(this.stop$),
      repeat({delay: () => this.start$})
    );
  }

  getArchived(): Observable<Referee[]> {
    return this.request('archive');
  }

  forceReload(): void {
    this.forceStop();
    this.start$.next();
  }

  forceStop(): void {
    this.stop$.next();
  }

  async archive(id: number): Promise<any> {
    await firstValueFrom(this.http.put<any>(ReferralsService.ARCHIVE_ITEM_API(id), null));
    this.forceReload();
  }

  getReferralsStatus(filter: string = null): Observable<any> {
    return timer(0).pipe(
      map(() => ({
        totalPages: 1,
        'referral-status': this.getReferralsStatusOptionList()
          .filter(t => filter ? t.label.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) : true)
      }))
    );
  }

  private request(vrType: string): Observable<Referee[]> {
    return this.http.get<ReferralProgramRefereesResponse>(this.getUrl(vrType))
      .pipe(
        tap(res => {
          this.sessionStorageService.changeTotalCount(res?.meta?.paging?.total_count || 0);
          this.sessionStorageService.changeTotalPages(res?.meta?.paging?.total_pages || 0);
        }),
        map(res => res?.referral_program_referees),
        map(res => res.map(r => new Referee(r)))
      );
  }

  private getUrl(mmType: string): string {
    if (mmType === 'dashboard') {
      return ReferralsService.ACTIVE_API() + this.queryCollectorService.getReferralsQuery();
    } else if (mmType === 'archive') {
      return ReferralsService.ARCHIVE_API() + this.queryCollectorService.getArchiveReferralsQuery();
    }
  }

  private getReferralsStatusOptionList() {
    const list: { label: string, value: string }[] = [];
    for (let hiringStateEnumKey in HiringStateEnum) {
      list.push({
        label: getStatusText(HiringStateEnum[hiringStateEnumKey]),
        value: hiringStateEnumKey
      });
    }
    return list;
  }

}
